import { useSelector, useDispatch } from "react-redux";

import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";

import AddLocationIcon from "@mui/icons-material/AddLocation";
import BrushIcon from "@mui/icons-material/Brush";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import HeightIcon from "@mui/icons-material/Height";
import LineWeightIcon from "@mui/icons-material/LineWeight";
import OpacityIcon from "@mui/icons-material/Opacity";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ColorizeIcon from "@mui/icons-material/Colorize";

import { actionSheetFileUpdated, selectorSheet } from "./spreadsheetRedux";
import { defaultSheetFile, returnSheetDefaults } from "./spreadsheetutils";
import {
  ColorConditional,
  TextConditional,
  IconConditional,
  PointConditional,
} from "./ConditionalFormatting";
import {
  SettingSlider,
  SettingColorSelector,
  SettingDropSelector,
  CollapseHeading,
  Setting,
} from "./SpreadsheetSettingSharedComponents";
import SharedIconSelector from "./IconSelector";
import { FONT_SIZE_MIN, FONT_SIZE_MAX } from "../Layer/LabelSettings";

const componentContainer_ = (theme) => ({
  paddingTop: "0px",
  paddingLeft: "15px",
  paddingRight: "15px",
  paddingBottom: "0px",
});

const settingController_ = (theme) => ({
  width: "55%",
});

const widthIcon_ = (theme) => ({
  transform: "rotate(90deg)",
});

const sizeIcon_ = (theme) => ({
  transform: "rotate(45deg)",
});

const hr_ = (theme) => ({
  backgroundColor: "lightgrey",
  height: 1,
  border: 0,
});

const settingExpandHeader_ = (theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "10px",
  paddingLeft: "10px",
  fontStyle: "italic",
  "&:hover": {
    cursor: "pointer",
  },
  "& :hover": {
    cursor: "pointer",
  },
  "& button": {
    padding: "0px",
    marginLeft: "4px",
  },
});

const switchChecked_ = (theme) => ({
  "& .Mui-checked": {
    color: "var(--fill-color)",
  },
});

const switchTrack_ = (theme) => ({
  backgroundColor: "var(--fill-color)",
});

const WidthIcon = () => {
  return <HeightIcon sx={widthIcon_} />;
};

const SizeIcon = () => {
  return <HeightIcon sx={sizeIcon_} />;
};

const ColorSelector = ({ sheetId, reduxKey, ...rest }) => {
  const { [reduxKey]: value } = useSelector(selectorSheet(sheetId));
  const { [reduxKey]: defaultValue } = defaultSheetFile(sheetId);
  const dispatch = useDispatch();

  const handleNewColor = (newColor) => {
    if (!newColor) return;
    let { r, g, b } = newColor.rgb;
    let rgb = `rgb(${r},${g},${b})`;
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        [reduxKey]: rgb,
      }),
    );
  };

  return (
    <SettingColorSelector
      fillColor={value || defaultValue}
      handleNewColor={handleNewColor}
      {...rest}
    />
  );
};

const SheetSlider = ({
  sheetId,
  reduxKey,
  displayValue = false,
  trackValue = true,
  vertical = false,
  ...rest
}) => {
  const { [reduxKey]: reduxValue } = useSelector(selectorSheet(sheetId));
  const { [reduxKey]: defaultValue } = defaultSheetFile(sheetId);
  const dispatch = useDispatch();
  const value = vertical ? -1 * reduxValue : reduxValue;
  const defaultVal = vertical ? -1 * defaultValue : defaultValue;

  const handleChange = (newValue) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        [reduxKey]: vertical ? -1 * newValue : newValue,
      }),
    );
  };

  const valueLabelDisplay = displayValue ? "auto" : "off";
  const track = trackValue && "normal";

  return (
    <SettingSlider
      value={value === null ? defaultVal : value}
      valueLabelDisplay={valueLabelDisplay}
      handleNewValue={handleChange}
      track={track}
      {...rest}
    />
  );
};

const TextSelector = ({ sheetId, ...rest }) => {
  const { text, json, defaultText } = useSelector(selectorSheet(sheetId));
  const dispatch = useDispatch();
  const textColumnHeaders = json[0].filter((header) => header);

  const textColumnHeaderObjects = textColumnHeaders.map((header, index) => {
    return {
      value: index,
      label: header,
    };
  });

  const handleSelection = (text) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        text,
      }),
    );
  };

  let textValue = text === null ? defaultText : text;

  return (
    <SettingDropSelector
      label="Attribute"
      value={textValue}
      handleSelection={handleSelection}
      optionsArray={textColumnHeaderObjects}
      defaultOption={{
        value: "",
        label: "No label",
      }}
      {...rest}
    />
  );
};

const IconSelector = ({ sheetId, ...rest }) => {
  const { icon } = useSelector(selectorSheet(sheetId));
  const dispatch = useDispatch();
  let iconVal = icon === null ? "" : icon;

  const handleSelection = (icon) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        icon,
      }),
    );
  };

  return (
    <SharedIconSelector
      value={iconVal}
      handleSelection={handleSelection}
      {...rest}
    />
  );
};

const PointIconSelector = ({ sheetId, ...rest }) => {
  const { pointIcon } = useSelector(selectorSheet(sheetId));
  const { pointIcon: defaultPointIcon } = defaultSheetFile(sheetId);
  const dispatch = useDispatch();
  let iconVal = pointIcon === null ? defaultPointIcon : pointIcon;

  const handleSelection = (icon) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        pointIcon: icon,
      }),
    );
  };

  return (
    <SharedIconSelector
      value={iconVal}
      handleSelection={handleSelection}
      {...rest}
    />
  );
};

const VisibilitySwitch = ({ sheetId, reduxKey }) => {
  const value = useSelector(selectorSheet(sheetId))[reduxKey];
  const dispatch = useDispatch();

  const handleSwitch = (event) => {
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        [reduxKey]: event.target.checked,
      }),
    );
  };

  return (
    <Setting
      active={true}
      label="Display"
      labelIcon={<VisibilityIcon />}
      controller={
        <FormGroup sx={settingController_}>
          <Switch
            sx={switchChecked_}
            slotProps={{
              input: {
                "aria-label": "Toggle text display",
              },
              track: {
                sx: switchTrack_,
              },
            }}
            checked={value}
            onChange={handleSwitch}
            color="default"
          />
        </FormGroup>
      }
    />
  );
};

const ResetButton = ({ sheetId, resetKeys, resetConditions, active }) => {
  const dispatch = useDispatch();
  const { conditions } = useSelector(selectorSheet(sheetId));

  const handleReset = () => {
    const resetObj = returnSheetDefaults(sheetId, ...resetKeys);
    const newConditions = conditions.filter(
      (condition) => !resetConditions.includes(condition.type),
    );

    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        ...resetObj,
        conditions: newConditions,
      }),
    );
  };

  return (
    <Setting
      active={active}
      label="Reset to defaults"
      labelIcon={<SettingsBackupRestoreIcon />}
      controller={
        <Button
          disabled={!active}
          onClick={handleReset}
          startIcon={<SettingsBackupRestoreIcon />}
          variant="contained"
        >
          Reset
        </Button>
      }
    />
  );
};

const SettingTab = ({
  defaultComponents,
  advancedComponents,
  conditionalComponent,
  type,
  sheetId,
}) => {
  const dispatch = useDispatch();

  const DEFAULT = type + "DefaultExpanded";
  const ADVANCED = type + "AdvancedExpanded";
  const CONDITIONAL = type + "ConditionalExpanded";
  const {
    [DEFAULT]: defaultExpanded,
    [ADVANCED]: advancedExpanded,
    [CONDITIONAL]: conditionalExpanded,
  } = useSelector(selectorSheet(sheetId));

  const handleExpand = (condition) => {
    let val;
    switch (condition) {
      case DEFAULT:
        val = !defaultExpanded;
        break;
      case ADVANCED:
        val = !advancedExpanded;
        break;
      case CONDITIONAL:
        val = !conditionalExpanded;
        break;
      default:
        return;
    }
    dispatch(
      actionSheetFileUpdated({
        id: sheetId,
        [condition]: val,
      }),
    );
  };

  return (
    <>
      <CollapseHeading
        headingClass={settingExpandHeader_}
        expanded={defaultExpanded}
        setExpanded={() => handleExpand(DEFAULT)}
        headingText="Default Settings"
      />
      <Collapse in={defaultExpanded}>
        <Box sx={componentContainer_}>{defaultComponents}</Box>
      </Collapse>
      <Box component="hr" sx={hr_} />
      <CollapseHeading
        headingClass={settingExpandHeader_}
        expanded={advancedExpanded}
        setExpanded={() => handleExpand(ADVANCED)}
        headingText="Advanced Settings"
      />
      <Collapse in={advancedExpanded}>
        <Box sx={componentContainer_}>{advancedComponents}</Box>
      </Collapse>
      <Box component="hr" sx={hr_} />
      <CollapseHeading
        headingClass={settingExpandHeader_}
        expanded={conditionalExpanded}
        setExpanded={() => handleExpand(CONDITIONAL)}
        headingText="Conditional Settings"
      />
      <Collapse in={conditionalExpanded}>
        <Box sx={componentContainer_}>{conditionalComponent}</Box>
      </Collapse>
    </>
  );
};

export const ColorTab = ({ sheetId }) => {
  const { fillVisible: active } = useSelector(selectorSheet(sheetId));
  return (
    <SettingTab
      sheetId={sheetId}
      type="color"
      defaultComponents={[
        <VisibilitySwitch
          sheetId={sheetId}
          reduxKey="fillVisible"
          key="fillVisible"
        />,
        <ColorSelector
          sheetId={sheetId}
          reduxKey="fillColor"
          label="Set Fill Color"
          key="fillColor"
          active={active}
          labelIcon={<FormatColorFillIcon />}
        />,
        <ResetButton
          sheetId={sheetId}
          resetKeys={[
            "fillVisible",
            "fillColor",
            "fillOpacity",
            "lineColor",
            "lineWidth",
          ]}
          resetConditions={["color", "heatmapColor"]}
          key="reset"
          active={active}
        />,
      ]}
      advancedComponents={[
        <ColorSelector
          sheetId={sheetId}
          reduxKey="lineColor"
          key="lineColor"
          label="Set Line Color"
          active={active}
          labelIcon={<BrushIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="fillOpacity"
          key="fillOpacity"
          min={0}
          max={1}
          step={0.1}
          label="Set Fill Opacity"
          active={active}
          labelIcon={<OpacityIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="lineWidth"
          key="lineWidth"
          min={0}
          max={15}
          label="Set Border Width"
          active={active}
          labelIcon={<LineWeightIcon />}
        />,
      ]}
      conditionalComponent={
        <ColorConditional active={active} sheetId={sheetId} />
      }
    />
  );
};

export const TextTab = ({ sheetId }) => {
  const { textVisible: active } = useSelector(selectorSheet(sheetId));

  return (
    <SettingTab
      type="text"
      sheetId={sheetId}
      defaultComponents={[
        <VisibilitySwitch
          sheetId={sheetId}
          reduxKey="textVisible"
          key="textVisible"
        />,
        <TextSelector
          sheetId={sheetId}
          key="text"
          active={active}
          labelIcon={<VpnKeyIcon />}
        />,
        <ResetButton
          sheetId={sheetId}
          resetKeys={[
            "textVisible",
            "text",
            "textX",
            "textY",
            "textSize",
            "textColor",
          ]}
          resetConditions={["text"]}
          key="reset"
          active={active}
        />,
      ]}
      advancedComponents={[
        <SheetSlider
          sheetId={sheetId}
          reduxKey="textX"
          key="textX"
          min={-5}
          max={5}
          step={0.25}
          trackValue={false}
          active={active}
          label="Horizontal"
          labelIcon={<WidthIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="textY"
          key="textY"
          min={-5}
          max={5}
          step={0.25}
          trackValue={false}
          vertical={true}
          label="Vertical"
          active={active}
          labelIcon={<HeightIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="textSize"
          key="textSize"
          min={FONT_SIZE_MIN}
          max={FONT_SIZE_MAX}
          label="Text Size"
          active={active}
          labelIcon={<FormatSizeIcon />}
        />,
        <ColorSelector
          sheetId={sheetId}
          reduxKey="textColor"
          key="textColor"
          label="Set Text Color"
          active={active}
          labelIcon={<FormatColorTextIcon />}
        />,
      ]}
      conditionalComponent={
        <TextConditional sheetId={sheetId} active={active} />
      }
    />
  );
};

export const IconTab = ({ sheetId }) => {
  const { iconVisible: active } = useSelector(selectorSheet(sheetId));

  return (
    <SettingTab
      type="icon"
      sheetId={sheetId}
      defaultComponents={[
        <VisibilitySwitch
          sheetId={sheetId}
          reduxKey="iconVisible"
          key="iconVisible"
        />,
        <IconSelector
          sheetId={sheetId}
          key="icon"
          active={active}
          labelIcon={<AddLocationIcon />}
        />,
        <ResetButton
          sheetId={sheetId}
          resetKeys={[
            "iconVisible",
            "icon",
            "iconSize",
            "iconX",
            "iconY",
            "iconColor",
          ]}
          resetConditions={["icon"]}
          key="reset"
          active={active}
        />,
      ]}
      advancedComponents={[
        <SheetSlider
          sheetId={sheetId}
          reduxKey="iconX"
          key="iconX"
          min={-5}
          max={5}
          step={0.25}
          trackValue={false}
          label="Horizontal"
          active={active}
          labelIcon={<WidthIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="iconY"
          key="iconY"
          min={-5}
          max={5}
          step={0.25}
          trackValue={false}
          vertical={true}
          label="Vertical"
          active={active}
          labelIcon={<HeightIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="iconSize"
          key="iconSize"
          min={0}
          max={3}
          step={0.1}
          label="Icon Size"
          active={active}
          labelIcon={<SizeIcon />}
        />,
        <ColorSelector
          sheetId={sheetId}
          active={active}
          reduxKey="iconColor"
          key="iconColor"
          label="Set Icon Color"
          labelIcon={<ColorizeIcon />}
        />,
      ]}
      conditionalComponent={
        <IconConditional sheetId={sheetId} active={active} />
      }
    />
  );
};

export const PointTab = ({ sheetId }) => {
  const { pointVisible: active } = useSelector(selectorSheet(sheetId));

  return (
    <SettingTab
      type="point"
      sheetId={sheetId}
      defaultComponents={[
        <VisibilitySwitch
          sheetId={sheetId}
          reduxKey="pointVisible"
          key="pointVisible"
        />,
        <PointIconSelector
          sheetId={sheetId}
          key="pointIcon"
          active={active}
          labelIcon={<AddLocationIcon />}
        />,
        <ResetButton
          sheetId={sheetId}
          resetKeys={[
            "pointVisible",
            "pointIcon",
            "pointColor",
            "pointOpacity",
            "pointSize",
          ]}
          resetConditions={["point", "heatmapPoint"]}
          key="reset"
          active={active}
        />,
      ]}
      advancedComponents={[
        <ColorSelector
          sheetId={sheetId}
          reduxKey="pointColor"
          label="Set Point Color"
          key="pointColor"
          active={active}
          labelIcon={<FormatColorFillIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="pointSize"
          key="pointSize"
          min={0}
          max={10}
          label="Icon Size"
          active={active}
          labelIcon={<SizeIcon />}
        />,
        <SheetSlider
          sheetId={sheetId}
          reduxKey="pointOpacity"
          key="pointOpacity"
          min={0}
          max={1}
          step={0.1}
          label="Icon Opacity"
          active={active}
          labelIcon={<OpacityIcon />}
        />,
      ]}
      conditionalComponent={
        <PointConditional sheetId={sheetId} active={active} />
      }
    />
  );
};
